import {
  DotsVertical,
  File06,
  Share07,
  Upload01,
  XClose,
} from '@untitledui/icons';
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';

import { ShareModal } from '@/components/sharing';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';

import { useDynamicView } from '../../hooks/useDynamicView';
import { exportToPdf, generatePdfFilename } from '../../utils/exportToPdf';
import { DynamicViewRenderer } from '../dynamic-views/DynamicViewRenderer';

interface DynamicViewSidebarProps {
  conversationId: number;
  orgId: string;
  viewId: number;
  onClose: () => void;
  /** Whether the current user is the creator of the conversation (for sharing permissions) */
  isCreator?: boolean;
}

interface SidebarChromeProps {
  title: string;
  /** Rendered to the left of the always-present close button. */
  actions?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * The frame around a dynamic view: title, actions, and — in every state — the
 * way out. Loading and error are states of the *body*, so they render beneath
 * this rather than replacing it. Previously each was its own early return with
 * no header at all, which left a panel occupying 40% of the window that the
 * user could not close: on a view that failed to load, the only escapes were
 * editing the URL or the browser Back button. Do not move the close button
 * into a branch of the body.
 */
const SidebarChrome: React.FC<SidebarChromeProps> = ({
  title,
  actions,
  onClose,
  children,
}) => (
  <div className='h-full flex flex-col bg-bg-secondary'>
    <div className='shrink-0'>
      <div className='flex items-center justify-between px-4 py-3'>
        <div className='flex items-center gap-2 min-w-0'>
          <File06
            className='w-6 h-6 text-fg-primary shrink-0'
            strokeWidth={1.5}
          />
          <h2 className='text-xl font-bold text-fg-primary truncate leading-tight'>
            {title}
          </h2>
        </div>
        <div className='flex items-center gap-1'>
          {actions}
          <button
            onClick={onClose}
            className='p-1 hover:bg-white/50 rounded-lg transition-colors cursor-pointer shrink-0'
            aria-label='Close'
          >
            <XClose className='w-5 h-5 text-fg-tertiary' />
          </button>
        </div>
      </div>
      <div className='border-t border-border-secondary' />
    </div>
    {children}
  </div>
);

/** Centred body used by the loading and error states. */
const SidebarMessage: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className='flex-1 flex items-center justify-center'>
    <div className='text-center'>
      <div className='w-8 h-8 bg-bg-primary flex items-center justify-center mb-3 mx-auto rounded'>
        <File06 className='w-4 h-4 text-fg-tertiary' />
      </div>
      <p className='text-sm text-fg-secondary'>{children}</p>
    </div>
  </div>
);

export const DynamicViewSidebar: React.FC<DynamicViewSidebarProps> = ({
  conversationId,
  orgId,
  viewId,
  onClose,
  isCreator = false,
}) => {
  const { view, loading, error } = useDynamicView(
    conversationId,
    orgId,
    viewId
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleExportPdf = async () => {
    if (!contentRef.current || !view) {
      return;
    }

    setIsMenuOpen(false);

    await exportToPdf({
      filename: generatePdfFilename(view.title),
      element: contentRef.current,
      onStart: () => setIsExporting(true),
      onComplete: () => {
        setIsExporting(false);
        toast.success('PDF exported successfully');
      },
      onError: error => {
        setIsExporting(false);
        toast.error("We couldn't export the PDF. Try again.");
        console.error('PDF export error:', error);
      },
    });
  };

  if (loading) {
    return (
      <SidebarChrome title='View' onClose={onClose}>
        <SidebarMessage>Loading view…</SidebarMessage>
      </SidebarChrome>
    );
  }

  if (error || !view) {
    return (
      <SidebarChrome title='View' onClose={onClose}>
        <SidebarMessage>
          {error || "We couldn't load this view. Try again."}
        </SidebarMessage>
      </SidebarChrome>
    );
  }

  return (
    <SidebarChrome
      title={view.title}
      onClose={onClose}
      actions={
        <div className='relative'>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className='p-1 hover:bg-white/50 rounded-lg transition-colors cursor-pointer shrink-0'
            aria-label='View options'
            aria-expanded={isMenuOpen}
          >
            <DotsVertical className='w-5 h-5 text-fg-tertiary' />
          </button>

          {isMenuOpen && (
            <>
              {/* Backdrop to close menu */}
              <div
                className='fixed inset-0 z-10'
                onClick={() => setIsMenuOpen(false)}
              />
              {/* Dropdown menu */}
              <div className='absolute right-0 top-full mt-1 z-20 bg-bg-primary rounded-lg shadow-lg border border-border-secondary py-1 min-w-[160px]'>
                <button
                  onClick={handleExportPdf}
                  disabled={isExporting}
                  className='w-full flex items-center gap-2 px-3 py-2 text-sm text-fg-secondary hover:bg-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {isExporting ? (
                    <LoadingIndicator size='xs' />
                  ) : (
                    <Upload01 className='w-4 h-4' />
                  )}
                  {isExporting ? 'Exporting…' : 'Export as PDF'}
                </button>
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    setIsShareModalOpen(true);
                  }}
                  className='w-full flex items-center gap-2 px-3 py-2 text-sm text-fg-secondary hover:bg-bg-secondary'
                >
                  <Share07 className='w-4 h-4' />
                  Share
                </button>
              </div>
            </>
          )}
        </div>
      }
    >
      {/* Content */}
      <div ref={contentRef} className='flex-1 overflow-auto p-4'>
        <DynamicViewRenderer
          view={view}
          interactive={true}
          className='max-w-none'
          skipFirstHeading
        />
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        orgId={parseInt(orgId)}
        objectType='dynamic_view'
        objectId={viewId}
        objectTitle={view.title}
        isCreator={isCreator}
      />
    </SidebarChrome>
  );
};

export default DynamicViewSidebar;
